import { graphOf, isSeaLink } from './map';
import { WILD_SYMBOL } from './types';
import type {
  AttackRoute,
  Effect,
  EffectKind,
  LootKind,
  MissionDef,
  PlayerId,
  RegionId,
  Ruleset,
  RulesView,
  ScoreLine,
  TerritoryId,
  VocationId,
} from './types';

/** Quem precisa agir agora (pendências têm prioridade sobre o jogador da vez). */
export function activePlayer(s: Pick<RulesView, 'pending' | 'current' | 'phase'>): PlayerId | null {
  if (s.phase === 'over') return null;
  if (s.pending) return s.pending.player;
  return s.current;
}

export function ownerOf(s: RulesView, t: TerritoryId): PlayerId {
  return s.territories[t].owner;
}

export function troopsOf(s: RulesView, t: TerritoryId): number {
  return s.territories[t].troops;
}

export function ownedTerritories(s: Pick<RulesView, 'territories'>, p: PlayerId): TerritoryId[] {
  const out: TerritoryId[] = [];
  for (const [id, t] of Object.entries(s.territories)) if (t.owner === p) out.push(id);
  return out;
}

export function territoryCount(s: Pick<RulesView, 'territories'>, p: PlayerId): number {
  let n = 0;
  for (const t of Object.values(s.territories)) if (t.owner === p) n++;
  return n;
}

export function totalTroops(s: Pick<RulesView, 'territories'>, p: PlayerId): number {
  let n = 0;
  for (const t of Object.values(s.territories)) if (t.owner === p) n += t.troops;
  return n;
}

export function neighborsOf(s: Pick<RulesView, 'info'>, t: TerritoryId): TerritoryId[] {
  return graphOf(s.info.content.map).neighbors.get(t) ?? [];
}

export function regionOf(s: Pick<RulesView, 'info'>, t: TerritoryId): RegionId {
  return graphOf(s.info.content.map).regionOf.get(t)!;
}

export function territoriesOfRegion(s: Pick<RulesView, 'info'>, r: RegionId): TerritoryId[] {
  return graphOf(s.info.content.map).territoriesOf.get(r) ?? [];
}

export function controlsRegion(s: Pick<RulesView, 'info' | 'territories'>, p: PlayerId, r: RegionId): boolean {
  const ts = territoriesOfRegion(s, r);
  return ts.length > 0 && ts.every((t) => s.territories[t].owner === p);
}

export function controlledRegions(s: Pick<RulesView, 'info' | 'territories'>, p: PlayerId): RegionId[] {
  return s.info.content.map.regions.filter((r) => controlsRegion(s, p, r.id)).map((r) => r.id);
}

export function vocationOf(s: Pick<RulesView, 'info' | 'players'>, p: PlayerId): VocationId | null {
  if (!s.info.ruleset.addons.vocations) return null;
  return s.players[p]?.vocation ?? null;
}

export function alivePlayers(s: Pick<RulesView, 'players'>): PlayerId[] {
  return s.players.filter((p) => p.alive).map((p) => p.id);
}

// ---------------------------------------------------------------- efeitos

export function effectsOn(s: Pick<RulesView, 'effects'>, t: TerritoryId, kind?: EffectKind): Effect[] {
  return s.effects.filter((e) => e.territory === t && (!kind || e.kind === kind));
}

export function hasEffect(s: Pick<RulesView, 'effects'>, t: TerritoryId, kind: EffectKind): boolean {
  return s.effects.some((e) => e.territory === t && e.kind === kind);
}

/** Passagem do Capitão ativa no turno de `p` (com o alvo travado depois do primeiro ataque). */
export function captainEffect(s: Pick<RulesView, 'effects'>, p: PlayerId): Effect | undefined {
  return s.effects.find((e) => e.kind === 'captain' && e.owner === p);
}

/** Efeito de turno ativo para o jogador (Berserk, Bullseye, Passagem do Capitão). */
export function hasTurnEffect(s: Pick<RulesView, 'effects'>, p: PlayerId, kind: EffectKind): boolean {
  return s.effects.some((e) => e.kind === kind && e.owner === p && !e.territory);
}

/** Pode soltar a rajada de harmonia agora. */
export function canUseHarmony(s: RulesView, p: PlayerId): boolean {
  if (vocationOf(s, p) !== 'harmony') return false;
  if (s.phase !== 'attack' || s.pending || s.current !== p || s.reinforcements > 0) return false;
  const me = s.players[p] as { harmony?: number };
  return (me.harmony ?? 0) >= s.info.ruleset.harmonyMin && !hasTurnEffect(s, p, 'flow');
}

export function isFrozen(s: Pick<RulesView, 'effects'>, t: TerritoryId): boolean {
  return hasEffect(s, t, 'frozen');
}

export function isParalyzed(s: Pick<RulesView, 'effects'>, t: TerritoryId): boolean {
  return hasEffect(s, t, 'paralyze');
}

/** Ligação bloqueada por Magic Wall (nos dois sentidos). */
export function isWalled(s: Pick<RulesView, 'effects'>, a: TerritoryId, b: TerritoryId): boolean {
  return s.effects.some(
    (e) => e.kind === 'wall' && e.link && ((e.link[0] === a && e.link[1] === b) || (e.link[0] === b && e.link[1] === a)),
  );
}

/** Vizinhos alcançáveis (ligações sem Magic Wall). */
export function openNeighbors(s: Pick<RulesView, 'info' | 'effects'>, t: TerritoryId): TerritoryId[] {
  return neighborsOf(s, t).filter((n) => !isWalled(s, t, n));
}

/** Pode atacar ou mover tropas a partir daqui (não congelado, não paralisado). */
export function canAct(s: Pick<RulesView, 'effects'>, t: TerritoryId): boolean {
  return !isFrozen(s, t) && !isParalyzed(s, t);
}

/** Territórios onde `p` pode colocar tropas: congelados não recebem, a menos que todos estejam. */
export function placeableTerritories(s: Pick<RulesView, 'territories' | 'effects'>, p: PlayerId): TerritoryId[] {
  const owned = ownedTerritories(s, p);
  const ok = owned.filter((t) => !isFrozen(s, t));
  return ok.length ? ok : owned;
}

// ---------------------------------------------------------------- reforços

export function reinforcementBreakdown(
  s: Pick<RulesView, 'info' | 'territories'>,
  p: PlayerId,
): { base: number; regions: { region: RegionId; bonus: number }[]; total: number } {
  const rs = s.info.ruleset;
  const base = Math.max(rs.minReinforcements, Math.floor(territoryCount(s, p) / rs.territoriesPerReinforcement));
  const regions = s.info.content.map.regions
    .filter((r) => controlsRegion(s, p, r.id))
    .map((r) => ({ region: r.id, bonus: r.bonus }));
  return { base, regions, total: base + regions.reduce((a, r) => a + r.bonus, 0) };
}

export function computeReinforcements(s: Pick<RulesView, 'info' | 'territories'>, p: PlayerId): number {
  return reinforcementBreakdown(s, p).total;
}

// ---------------------------------------------------------------- cartas

/** Valor da troca de número `index` (0 = primeira troca da partida). */
export function tradeValue(rs: Ruleset, index: number): number {
  const v = rs.tradeValues;
  if (index < v.length) return v[index];
  return v[v.length - 1] + (index - v.length + 1) * rs.tradeIncrement;
}

/** 3 iguais ou 3 diferentes. O curinga vale qualquer símbolo (no máximo 2 por troca). */
export function isValidSet(symbols: string[]): boolean {
  if (symbols.length !== 3) return false;
  const wilds = symbols.filter((x) => x === WILD_SYMBOL).length;
  if (wilds > 2) return false;
  if (wilds > 0) return true;
  const distinct = new Set(symbols).size;
  return distinct === 1 || distinct === 3;
}

export function symbolOf(s: Pick<RulesView, 'info'>, card: TerritoryId): string {
  return graphOf(s.info.content.map).symbolOf.get(card)!;
}

/** Todas as combinações de 3 cartas válidas na mão. */
export function validTrades(s: Pick<RulesView, 'info'>, hand: TerritoryId[]): TerritoryId[][] {
  const out: TerritoryId[][] = [];
  for (let i = 0; i < hand.length; i++)
    for (let j = i + 1; j < hand.length; j++)
      for (let k = j + 1; k < hand.length; k++) {
        const cards = [hand[i], hand[j], hand[k]];
        if (isValidSet(cards.map((c) => symbolOf(s, c)))) out.push(cards);
      }
  return out;
}

export function mustTrade(s: RulesView, p: PlayerId): boolean {
  const hand = s.players[p].cards;
  return (
    s.phase === 'reinforce' &&
    s.current === p &&
    !!hand &&
    hand.length >= s.info.ruleset.mandatoryTradeHand
  );
}

// ---------------------------------------------------------------- ataque

export interface RouteInfo {
  route: AttackRoute;
  /** O ataque passa por alguma rota de barco (com o add-on ligado). */
  sea: boolean;
  via: TerritoryId | null;
}

/** Como `p` pode atacar `to` a partir de `from`, ou null se não pode. */
export function attackRoute(s: RulesView, p: PlayerId, from: TerritoryId, to: TerritoryId): RouteInfo | null {
  const ft = s.territories[from];
  const tt = s.territories[to];
  if (!ft || !tt || from === to) return null;
  if (ft.owner !== p || tt.owner === p) return null;
  const map = s.info.content.map;
  const seaOn = s.info.ruleset.addons.sea;
  const options: RouteInfo[] = [];
  if (openNeighbors(s, from).includes(to)) {
    const sea = seaOn && isSeaLink(map, from, to);
    options.push({ route: sea ? 'sea' : 'land', sea, via: null });
  }
  if (vocationOf(s, p) === 'range') {
    for (const mid of openNeighbors(s, from)) {
      if (mid === to || s.territories[mid].owner !== p) continue;
      if (!openNeighbors(s, mid).includes(to)) continue;
      const sea = seaOn && (isSeaLink(map, from, mid) || isSeaLink(map, mid, to));
      options.push({ route: 'range', sea, via: mid });
    }
  }
  if (!options.length) {
    // Passagem do Capitão: de um porto seu a um porto inimigo, como se houvesse rota de barco.
    const cap = captainEffect(s, p);
    if (cap) {
      const ports = graphOf(map).ports;
      const locked = !cap.link || (cap.link[0] === from && cap.link[1] === to);
      if (locked && ports.has(from) && ports.has(to)) return { route: 'captain', sea: false, via: null };
    }
    return null;
  }
  // Preferir o caminho sem barco (melhor para o atacante); direto antes do alcance.
  return options.find((o) => !o.sea) ?? options[0];
}

export function attackDiceCount(s: RulesView, from: TerritoryId, sea: boolean): number {
  const rs = s.info.ruleset;
  let n = Math.min(rs.maxAttackDice, s.territories[from].troops - 1);
  if (sea) n = Math.min(n, rs.seaMaxDice);
  return Math.max(0, n);
}

export function defenseDiceCount(s: RulesView, to: TerritoryId): number {
  return Math.min(s.info.ruleset.maxDefenseDice, s.territories[to].troops);
}

/** Trégua de era: até `truceRounds` ninguém ataca outro jogador (bosses continuam liberados). */
export function truceActive(s: Pick<RulesView, 'round' | 'info'>): boolean {
  return s.round <= s.info.ruleset.truceRounds;
}

export function canAttack(s: RulesView, p: PlayerId, from: TerritoryId, to: TerritoryId): boolean {
  if (s.phase !== 'attack' || s.pending || s.current !== p || s.reinforcements > 0) return false;
  if (truceActive(s)) return false;
  if ((s.territories[from]?.troops ?? 0) < 2) return false;
  if (!canAct(s, from)) return false;
  return attackRoute(s, p, from, to) !== null;
}

/** Territórios que podem ser alvo a partir de `from`: vizinhos, a 2 passos (alcance) e portos (Capitão). */
function attackCandidates(s: RulesView, p: PlayerId, from: TerritoryId): Iterable<TerritoryId> {
  const out = new Set<TerritoryId>(neighborsOf(s, from));
  if (vocationOf(s, p) === 'range') for (const mid of neighborsOf(s, from)) for (const n of neighborsOf(s, mid)) out.add(n);
  if (captainEffect(s, p)) for (const t of graphOf(s.info.content.map).ports) out.add(t);
  out.delete(from);
  return out;
}

export function getAttackTargets(s: RulesView, p: PlayerId, from: TerritoryId): TerritoryId[] {
  if (s.phase !== 'attack' || s.pending || s.current !== p || s.reinforcements > 0) return [];
  if ((s.territories[from]?.troops ?? 0) < 2 || s.territories[from].owner !== p) return [];
  return [...attackCandidates(s, p, from)].filter((to) => canAttack(s, p, from, to));
}

export function canAttackBoss(s: RulesView, p: PlayerId, from: TerritoryId): boolean {
  if (s.phase !== 'attack' || s.pending || s.current !== p || s.reinforcements > 0) return false;
  if (!s.boss || !s.info.ruleset.addons.bosses) return false;
  const t = s.territories[from];
  if (!t || t.owner !== p || t.troops < 2) return false;
  if (!canAct(s, from)) return false;
  return lairAdjacent(s, s.boss.region).includes(from);
}

export function lairAdjacent(s: Pick<RulesView, 'info'>, region: RegionId): TerritoryId[] {
  return graphOf(s.info.content.map).lairOf.get(region) ?? [];
}

// ---------------------------------------------------------------- remanejar

/** Tropas que ainda podem sair de `t` neste turno. */
export function movableTroops(s: Pick<RulesView, 'territories' | 'turn'>, t: TerritoryId): number {
  const st = s.territories[t];
  const arrived = s.turn.movedIn[t] ?? 0;
  return Math.max(0, Math.min(st.troops - 1, st.troops - arrived));
}

export function fortifyTargets(s: RulesView, p: PlayerId, from: TerritoryId): TerritoryId[] {
  if (s.territories[from]?.owner !== p || !canAct(s, from)) return [];
  return openNeighbors(s, from).filter((n) => s.territories[n].owner === p && !isFrozen(s, n) && !isParalyzed(s, n));
}

/** Pode receber tropas movidas (remanejamento ou teleporte). */
export function canReceiveMove(s: Pick<RulesView, 'effects'>, t: TerritoryId): boolean {
  return !isFrozen(s, t) && !isParalyzed(s, t);
}

// ---------------------------------------------------------------- loot e vocações

export function hasLoot(s: RulesView, p: PlayerId, kind: LootKind): boolean {
  return s.players[p].loot?.includes(kind) ?? false;
}

/** Alvos válidos para o poder de dano (remove 1 tropa de um alvo de ataque). */
export function powerTargets(s: RulesView, p: PlayerId): TerritoryId[] {
  if (vocationOf(s, p) !== 'damage' || s.turn.powerUsed) return [];
  if (s.phase !== 'attack' || s.pending || s.current !== p || s.reinforcements > 0) return [];
  const targets = new Set<TerritoryId>();
  for (const from of ownedTerritories(s, p)) {
    if (s.territories[from].troops < 2) continue;
    for (const to of getAttackTargets(s, p, from)) if (s.territories[to].troops >= 2) targets.add(to);
  }
  return [...targets];
}

// ---------------------------------------------------------------- missões e pontos

export function isMissionComplete(
  s: Pick<RulesView, 'info' | 'territories' | 'players'>,
  p: PlayerId,
  mission: MissionDef,
): boolean {
  switch (mission.type) {
    case 'regions': {
      if (!mission.regions.every((r) => controlsRegion(s, p, r))) return false;
      const extra = mission.plusAny ?? 0;
      if (!extra) return true;
      const others = controlledRegions(s, p).filter((r) => !mission.regions.includes(r));
      return others.length >= extra;
    }
    case 'ports': {
      const ports = graphOf(s.info.content.map).ports;
      let n = 0;
      for (const t of ports) if (s.territories[t]?.owner === p) n++;
      return n >= mission.count;
    }
    case 'territories': {
      const min = mission.minTroops ?? 1;
      let n = 0;
      for (const t of Object.values(s.territories)) if (t.owner === p && t.troops >= min) n++;
      return n >= mission.count;
    }
    case 'bossKills':
      return s.players[p].bossKills >= mission.count;
    case 'eliminate': {
      const target = s.players.find((pl) => pl.color === mission.color);
      return !!target && !target.alive && target.eliminatedBy === p;
    }
  }
}

export function getScores(s: Pick<RulesView, 'info' | 'territories' | 'players'>): ScoreLine[] {
  const rs = s.info.ruleset;
  return s.players.map((pl) => {
    const territories = territoryCount(s, pl.id);
    const regions = controlledRegions(s, pl.id).length;
    const troops = totalTroops(s, pl.id);
    return {
      player: pl.id,
      territories,
      regions,
      bossKills: pl.bossKills,
      troops,
      score:
        territories * rs.scorePerTerritory + regions * rs.scorePerRegion + pl.bossKills * rs.scorePerBossKill,
    };
  });
}

export function getScore(s: Pick<RulesView, 'info' | 'territories' | 'players'>, p: PlayerId): number {
  return getScores(s).find((l) => l.player === p)?.score ?? 0;
}

/** Ordena pela pontuação, desempate por tropas e depois pela ordem dos lugares. */
export function rankScores(lines: ScoreLine[]): ScoreLine[] {
  return lines.slice().sort((a, b) => b.score - a.score || b.troops - a.troops || a.player - b.player);
}
